/**
 * Escapes special characters in a string to prevent XSS.
 * This should be used for any user-provided content that is rendered
 * via dangerouslySetInnerHTML or similar.
 */
export function escapeHtml(unsafe: string): string {
	if (!unsafe) return "";
	return unsafe
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#039;");
}

/**
 * Safely serializes data to JSON for use inside <script> tags.
 * Escapes special characters < and > to prevent XSS.
 */
export function safeJSON(data: unknown): string {
	return JSON.stringify(data).replace(/[<>]/g, c => {
		return c === "<" ? "\\u003c" : "\\u003e";
	});
}

/**
 * Redacts sensitive query parameters from a URL string to prevent sensitive data leakage in logs.
 * Supports absolute URLs and relative path+search strings.
 *
 * @param urlStr The URL string to redact (absolute or relative)
 * @returns The redacted URL string
 */
export function redactSensitiveParams(urlStr: string): string {
	if (!urlStr) return "";
	try {
		// List of case-insensitive keys to redact
		const sensitiveKeys = [
			"token",
			"password",
			"secret",
			"authorization",
			"tid",
			"access_token",
			"refresh_token",
			"api_key",
			"apikey",
			"otp",
		];

		// Check if it's a relative URL (doesn't start with http protocol)
		const isRelative = !urlStr.match(/^https?:\/\//i);
		// Use a dummy base for parsing relative URLs if needed
		const url = new URL(urlStr, "http://dummy.com");

		let changed = false;
		url.searchParams.forEach((_, key) => {
			if (sensitiveKeys.includes(key.toLowerCase())) {
				url.searchParams.set(key, "[REDACTED]");
				changed = true;
			}
		});

		if (!changed) return urlStr;

		if (isRelative) {
			// Return pathname + search for relative URLs
			return url.pathname + url.search;
		}
		// Return full URL for absolute URLs
		return url.toString();
	} catch {
		// Fallback: return original if parsing fails
		return urlStr;
	}
}
